'use client';

import { useState, type JSX } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/lib/contexts/LanguageContext';

interface BusinessInfoProps {
  formData: {
    industry: string;
    numEmployees: string;
    annualRevenue: string;
    businessDescription: string;
    registeredCapital: string;
    [key: string]: string | boolean;
  };
  onChangeAction: (section: string, field: string, value: string | boolean) => void;
}

function getStringFieldValue(
  formData: BusinessInfoProps['formData'],
  field: string,
): string | undefined {
  const value = formData[field];
  return typeof value === 'string' ? value : undefined;
}

export default function BusinessInfo({
  formData,
  onChangeAction,
}: Readonly<BusinessInfoProps>): JSX.Element {
  const { t } = useLanguage();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

  const validateField = (field: string, value: string): boolean => {
    let error = '';

    // First check if field is empty
    if (value.trim() === '') {
      error = t.fieldRequired;
    } else {
      // Field-specific validations
      switch (field) {
        case 'businessDescription':
          if (value.length < 50) {
            error = t.descriptionMinLength;
          }
          break;
        case 'registeredCapital':
          if (!/^\$?[\d,]+(\.\d{1,2})?$/.test(value) && !/^[\d,]+(\.\d{1,2})?$/.test(value)) {
            error = t.invalidMonetary;
          }
          break;
      }
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return error === '';
  };

  const markFieldAsDirty = (field: string): void => {
    setDirtyFields((prev) => ({ ...prev, [field]: true }));
  };

  const handleBlur = (field: string): void => {
    markFieldAsDirty(field);
    const value = getStringFieldValue(formData, field);
    if (value !== undefined) {
      validateField(field, value);
    }
  };

  // Helper function to determine if we should show an error
  const shouldShowError = (field: string): boolean | undefined => {
    return dirtyFields[field] && Boolean(errors[field]);
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">{t.businessDetailsTitle}</h3>
      <p className="text-sm text-gray-500">{t.provideBusinessInfo}</p>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="industry" className="flex items-center">
            {t.industry} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Select
            value={formData.industry}
            onValueChange={(value) => onChangeAction('business', 'industry', value)}
            required
          >
            <SelectTrigger
              id="industry"
              className={shouldShowError('industry') ? 'border-red-500' : ''}
              onBlur={() => handleBlur('industry')}
            >
              <SelectValue placeholder={t.industryPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="technology">{t.technology}</SelectItem>
              <SelectItem value="manufacturing">{t.manufacturing}</SelectItem>
              <SelectItem value="retail">{t.retail}</SelectItem>
              <SelectItem value="healthcare">{t.healthcare}</SelectItem>
              <SelectItem value="finance">{t.financialServices}</SelectItem>
              <SelectItem value="education">{t.education}</SelectItem>
              <SelectItem value="other">{t.other}</SelectItem>
            </SelectContent>
          </Select>
          {shouldShowError('industry') && <p className="text-sm text-red-500">{errors.industry}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="numEmployees" className="flex items-center">
            {t.numberOfEmployees} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Select
            value={formData.numEmployees}
            onValueChange={(value) => onChangeAction('business', 'numEmployees', value)}
            required
          >
            <SelectTrigger
              id="numEmployees"
              className={shouldShowError('numEmployees') ? 'border-red-500' : ''}
              onBlur={() => handleBlur('numEmployees')}
            >
              <SelectValue placeholder={t.employeesPlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1-10">1-10</SelectItem>
              <SelectItem value="11-50">11-50</SelectItem>
              <SelectItem value="51-200">51-200</SelectItem>
              <SelectItem value="201-500">201-500</SelectItem>
              <SelectItem value="501+">501+</SelectItem>
            </SelectContent>
          </Select>
          {shouldShowError('numEmployees') && (
            <p className="text-sm text-red-500">{errors.numEmployees}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="annualRevenue" className="flex items-center">
            {t.annualRevenue} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Select
            value={formData.annualRevenue}
            onValueChange={(value) => onChangeAction('business', 'annualRevenue', value)}
            required
          >
            <SelectTrigger
              id="annualRevenue"
              className={shouldShowError('annualRevenue') ? 'border-red-500' : ''}
              onBlur={() => handleBlur('annualRevenue')}
            >
              <SelectValue placeholder={t.revenuePlaceholder} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="<100k">{t.lessAmount}</SelectItem>
              <SelectItem value="100k-500k">{t.amount100k}</SelectItem>
              <SelectItem value="500k-1m">{t.amount500k}</SelectItem>
              <SelectItem value="1m-10m">{t.amount1m}</SelectItem>
              <SelectItem value="10m+">{t.amount10m}</SelectItem>
            </SelectContent>
          </Select>
          {shouldShowError('annualRevenue') && (
            <p className="text-sm text-red-500">{errors.annualRevenue}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="businessDescription" className="flex items-center">
            {t.businessDescription} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Textarea
            id="businessDescription"
            placeholder={t.descriptionPlaceholder}
            value={formData.businessDescription}
            onChange={(e) => onChangeAction('business', 'businessDescription', e.target.value)}
            onBlur={() => handleBlur('businessDescription')}
            className={`min-h-24 ${shouldShowError('businessDescription') ? 'border-red-500' : ''}`}
            required
          />
          {shouldShowError('businessDescription') && (
            <p className="text-sm text-red-500">{errors.businessDescription}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.descriptionHelp}</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="registeredCapital" className="flex items-center">
            {t.registeredCapital} <span className="ml-1 text-red-500">*</span>
          </Label>
          <Input
            id="registeredCapital"
            placeholder={t.capitalPlaceholder}
            value={formData.registeredCapital}
            onChange={(e) => onChangeAction('business', 'registeredCapital', e.target.value)}
            onBlur={() => handleBlur('registeredCapital')}
            className={shouldShowError('registeredCapital') ? 'border-red-500' : ''}
            required
          />
          {shouldShowError('registeredCapital') && (
            <p className="text-sm text-red-500">{errors.registeredCapital}</p>
          )}
          <p className="text-muted-foreground text-sm">{t.capitalHelp}</p>
        </div>
      </div>
    </div>
  );
}
